import React, { Component } from "react";
import { Link } from "react-router-dom";

import "../../styles/newDesign.scss";
import "../../components/HomeBlocks/HomeBlocks.scss";
import "./Calendar.scss";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

import { resolveTenant, isLoggedIn, calendarMonth, calendarToday, tickEntry } from "../../Helper/Api";
import { t, getLanguage } from "../../Helper/i18n";
import { byDay, monthCells, shiftMonth, todayCairo, durationLabel } from "../../Helper/library";
import Toast from "../../components/Toast/Toast";

const properties = require("../../properties.json");

/** The member's workout calendar: today's workout first, then the month with a tick on each day's entries. */
class Calendar extends Component {
  state = { tenant: null, notFound: false, month: todayCairo().slice(0, 7), data: null, today: null, loading: true, error: null };

  componentDidMount() {
    if (!isLoggedIn()) {
      window.location.href = "/sign-in";
      return;
    }
    resolveTenant()
      .then((tenant) => {
        document.title = properties.site_name + " - " + t("cal.title");
        this.setState({ tenant });
        this.load();
      })
      .catch(() => this.setState({ notFound: true }));
  }

  load = () => {
    this.setState({ loading: true });
    Promise.all([calendarMonth(this.state.month), calendarToday(todayCairo())])
      .then(([data, today]) => this.setState({ data, today, loading: false, error: null }))
      .catch((e) => this.setState({ loading: false, error: e.message }));
  };

  go = (step) => this.setState({ month: shiftMonth(this.state.month, step) }, this.load);

  tick = (entry) => {
    const on = !entry.done;
    tickEntry(entry.id, on)
      .then(() => {
        const flip = (m) => (m ? { ...m, entries: m.entries.map((e) => (e.id === entry.id ? { ...e, done: on } : e)), done: m.entries.filter((e) => (e.id === entry.id ? on : e.done)).length } : m);
        this.setState((s) => ({ data: flip(s.data), today: flip(s.today) }));
      })
      .catch((e) => this.setState({ error: e.message }));
  };

  entryLink(e) {
    if (e.courseId) return `/courses/${e.courseId}${e.itemId ? `?item=${e.itemId}` : ""}`;
    return null;
  }

  renderEntry = (e, compact) => {
    const link = this.entryLink(e);
    return (
      <li key={e.id} className={`cal-entry ${e.done ? "done" : ""}`}>
        <button type="button" className="cal-tick" aria-pressed={e.done} aria-label={t("cal.markDone")} onClick={() => this.tick(e)}>
          <i className={e.done ? "fas fa-check-circle" : "far fa-circle"} aria-hidden="true" />
        </button>
        <span className="cal-entry-body">
          {link ? <Link to={link}>{e.title}</Link> : <span>{e.title}</span>}
          {!compact && e.durationSeconds ? <small>{durationLabel(e.durationSeconds)}</small> : null}
          {!compact && e.note ? <small className="cal-note">{e.note}</small> : null}
        </span>
      </li>
    );
  };

  render() {
    const { tenant, notFound, month, data, today, loading, error } = this.state;
    if (notFound) return <div className="page"><div className="container text-center"><p>{t("notFound")}</p></div></div>;
    if (!tenant) return null;

    const en = getLanguage() === "en";
    const weekStart = en ? 1 : 6;                                  // Monday for English, Saturday for Arabic
    const names = en ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] : ["أحد", "اتنين", "تلات", "أربع", "خميس", "جمعة", "سبت"];
    const header = Array.from({ length: 7 }, (_, i) => names[(weekStart + i) % 7]);
    const cells = monthCells(month, weekStart);
    const days = byDay(data && data.entries);
    const todayKey = todayCairo();
    const label = new Date(`${month}-15T12:00:00Z`).toLocaleDateString(en ? "en-GB" : "ar-EG-u-nu-latn", { month: "long", year: "numeric", timeZone: "UTC" });

    return (
      <>
        <Header tenant={tenant} />
        <div className="nd-page hb-page calendar">
          <div className="hb-wrap">
            <h1 className="hb-title nd-title">{t("cal.title")}</h1>
            <Toast msg={error} type="error" />

            <section className="cal-today">
              <h2>{t("cal.today")}</h2>
              {today && today.entries.length > 0 ? (
                <ul className="cal-list">{today.entries.map((e) => this.renderEntry(e, false))}</ul>
              ) : (
                <p className="hb-muted">{loading ? t("loading") : t("cal.nothingToday")}</p>
              )}
            </section>

            <div className="cal-head">
              <button type="button" className="hb-btn hb-btn-ghost" onClick={() => this.go(-1)} aria-label={t("cal.prev")}>‹</button>
              <h2>{label}</h2>
              <button type="button" className="hb-btn hb-btn-ghost" onClick={() => this.go(1)} aria-label={t("cal.next")}>›</button>
              {data && data.total > 0 && <span className="hb-chip cal-progress">{t("cal.progress", { done: data.done, total: data.total })}</span>}
            </div>

            <div className="cal-grid" role="grid">
              {header.map((n) => <div className="cal-dow" key={n}>{n}</div>)}
              {cells.map((day, i) => (
                <div className={`cal-day ${day === todayKey ? "today" : ""} ${day ? "" : "blank"}`} key={day || `b${i}`} role="gridcell">
                  {day && <b>{Number(day.slice(8))}</b>}
                  {day && days[day] && <ul>{days[day].map((e) => this.renderEntry(e, true))}</ul>}
                </div>
              ))}
            </div>
            {data && data.total === 0 && !loading && <p className="hb-muted cal-empty">{t("cal.empty")}</p>}
          </div>
        </div>
        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Calendar;
